<template>
    <div>
        <PageHeader>
            <template v-if="issue && issue.issue">{{ issue.issue.id }}</template>
            <template #side>
                <ButtonIcon :to="parentRoute">
                    {{ $t('goBack') }}
                </ButtonIcon>
            </template>
        </PageHeader>

        <Card :header="$t('details')" overlay :loading="requestIssue.getLoading(`patch-${id}`) || requestIssueDetails.getLoading() || requestVariantWithProduct.getLoading()">
            <ErrorMessage :value="requestIssueDetails.getError()" />

            <div v-if="issue" class="grid-template">
                <div class="label">{{ $t('id') }}:</div>
                <div class="value">{{ issue.issue.id }}</div>

                <div class="label">{{ $t('status') }}:</div>
                <div class="value">{{ $tx(`catalogIssueStatuses.${issue.issue.status}`, issue.issue.status) }}</div>

                <div class="label">{{ $t('type') }}:</div>
                <div class="value">{{ issue.issue.type }}</div>

                <div class="label">{{ $t('createdAt') }}:</div>
                <div class="value">{{ $format.datetime(issue.issue.createdAt) }}</div>

                <div class="label">{{ $t('comment') }}:</div>
                <div class="value">{{ issue.issue.comment }}</div>

                <div class="label">{{ $t('source') }}:</div>
                <div class="value">{{ issue.issue.importedVariant.source }}</div>

                <div class="label">{{ $t('sourceId') }}:</div>
                <div class="value">{{ issue.issue.importedVariant.sourceId }}</div>

                <div class="label">{{ $t('ean') }}:</div>
                <div class="value">{{ issue.issue.importedVariant.ean }}</div>

                <div class="label">{{ $t('name') }}:</div>
                <div class="value">{{ issue.issue.importedVariant.name }}</div>

                <div class="label">{{ $t('author') }}:</div>
                <div class="value">{{ issue.issue.importedVariant.author }}</div>

                <div class="label">{{ $t('otherVariants') }}:</div>
                <div class="value">
                    <div class="d-flex flex-wrap gap-column-3">
                        <b-link v-for="(ean, key) in issue.issue.importedVariant.otherVariants" :key="key" :to="{ name: 'Offers', query: { ean: ean } }">{{ ean }}</b-link>
                    </div>
                </div>
            </div>
        </Card>

        <Card
            :header="$t('catalogSuggestion')"
            overlay
            :loading="requestIssue.getLoading(`patch-${id}`) || requestIssueDetails.getLoading() || requestVariantWithProduct.getLoading()"
        >
            <template #nav v-if="conflictedProducts">
                <div class="d-flex gap-2">
                    <ButtonIcon variant="success" @click="confirmCreate()">
                        {{ $t('createProduct') }}
                    </ButtonIcon>
                    <ButtonIcon variant="warning" @click="confirmIgnore()">
                        {{ $t('ignoreProposal') }}
                    </ButtonIcon>
                    <!-- <ButtonIcon variant="primary" @click="confirmEdit()">
                        {{ $t('editProposal') }}
                    </ButtonIcon> -->
                </div>
            </template>

            <template v-if="conflictedProducts">
                <template v-for="conflictedProduct in conflictedProducts">
                    <div :key="conflictedProduct.id" class="mb-5">
                        <b-table-simple small borderless class="mb-0">
                            <b-thead class="thead-dark">
                                <b-tr class="border-bottom">
                                    <b-th>{{ $t('id') }}</b-th>
                                    <b-th>{{ $t('name') }}</b-th>
                                    <b-th>{{ $t('author') }}</b-th>
                                    <b-th>{{ $t('brand') }}</b-th>
                                    <b-th>{{ $t('description') }}</b-th>
                                    <b-th>{{ $t('source') }}</b-th>
                                </b-tr>
                            </b-thead>
                            <b-tbody>
                                <b-tr>
                                    <b-td>{{ conflictedProduct.id }}</b-td>
                                    <b-td>{{ conflictedProduct.name }}</b-td>
                                    <b-td>{{ conflictedProduct.author }}</b-td>
                                    <b-td>{{ conflictedProduct.brand }}</b-td>
                                    <b-td>{{ conflictedProduct.description }}</b-td>
                                    <b-td>{{ conflictedProduct.source }}</b-td>
                                </b-tr>
                            </b-tbody>
                        </b-table-simple>

                        <h6>{{ $t('variants') }}</h6>
                        <b-table-lite
                            :items="variants"
                            :fields="variantsFields"
                            small
                            responsive
                            thead-tr-class="border-bottom"
                            head-variant="secondary"
                            details-td-class="border-bottom border-light"
                        >
                            <template #head()="scope">{{ $tx(camelCase(scope.label), scope.label) }}</template>
                            <template #row-details="{ item }">
                                <b-table-simple hover small responsive borderless class="m-0">
                                    <b-thead>
                                        <b-tr>
                                            <b-th>
                                                <div class="d-flex gap-2 align-items-center accordion">
                                                    <ButtonIcon
                                                        v-b-toggle="`accordion-${item.variants.map(item => item.gtin).join('-')}`"
                                                        variant="secondary"
                                                        icon="chevron-down"
                                                        flip-icon
                                                        size="sm"
                                                        class="expandable p-1"
                                                        :disabled="!!getOffersLoading(item.variants.map(item => item.gtin)).length"
                                                    />
                                                    <span>{{ $t('offers') }}:</span>
                                                    <b-spinner v-if="!!getOffersLoading(item.variants.map(item => item.gtin)).length" small />
                                                    <Tooltip
                                                        v-else-if="!!getOffersError(item.variants.map(item => item.gtin)).length"
                                                        type="alert-circle"
                                                        :size="20"
                                                        variant="danger"
                                                        icon-variant="danger"
                                                        :html="getOffersErrorTooltipHtml(item.variants.map(item => item.gtin))"
                                                    />
                                                </div>
                                            </b-th>
                                        </b-tr>
                                    </b-thead>
                                </b-table-simple>
                                <b-collapse :id="`accordion-${item.variants.map(item => item.gtin).join('-')}`" visible>
                                    <DataTable
                                        v-if="!getOffersLoading(item.variants.map(item => item.gtin)).length"
                                        :items="item.variants.map(item => requestOffer.getData(item.gtin).items).flat(10)"
                                        :fields="offersFields"
                                        small
                                        borderless
                                        responsive
                                        thead-tr-class="border-bottom"
                                        :pagination="false"
                                        no-padding
                                    >
                                        <template #empty>
                                            <div class="f-flex text-center">{{ $t('noOffers') }}</div>
                                        </template>
                                        <template #cell(product)="data">
                                            <div class="d-flex flex-column gap-2">
                                                <b-link v-if="data.item.product" :to="{ name: 'Offers', query: { product: data.item.product } }">
                                                    {{ data.item.product }}
                                                </b-link>
                                                <div class="info text-secondary font-size-12">
                                                    {{ data.item.offerId }}
                                                </div>
                                            </div>
                                        </template>

                                        <template #cell(ean)="data">
                                            <b-link :to="{ name: 'Offers', query: { ean: data.item.ean } }">
                                                {{ data.item.ean }}
                                            </b-link>
                                        </template>

                                        <template #cell(name)="data">
                                            <div class="d-flex">
                                                <ImageLoader :src="data.item.image" alt="" size="50" image-height="auto" class="mr-2 flex-shrink-0" />
                                                <div class="d-flex flex-column gap-2">
                                                    <b-link class="title" :to="createItemUrl(data.item)">
                                                        {{ data.item.name }}
                                                    </b-link>
                                                    <div class="info text-secondary font-size-12">
                                                        {{ data.item.categories.join(',') }}
                                                    </div>
                                                </div>
                                            </div>
                                        </template>

                                        <template #cell(feed)="data">
                                            <div class="d-flex flex-column gap-2">
                                                {{ data.item.feed }}
                                                <div v-if="data.item.campaign" class="info">
                                                    {{ data.item.campaign.name }}
                                                </div>
                                            </div>
                                        </template>

                                        <template #cell(price)="data">
                                            <div class="nowrap">
                                                {{ $format.currency(data.item.price) }}
                                            </div>
                                        </template>

                                        <template #cell(type)="data">
                                            {{ getTypeLabel(data.item.type) }}<br>
                                            <template v-if="data.item.format">{{ data.item.format }}</template>
                                        </template>

                                        <template #cell(hidden)="data">
                                            <BadgeHidden :value="data.item.hidden" />
                                        </template>

                                        <template #cell(active)="data">
                                            <div class="d-flex flex-column gap-2">
                                                <div><BadgeBoolean :value="data.item.active" :true-value-text="$t('available2')" :false-value-text="$t('unavailable2')" /></div>
                                                <div class="info text-secondary font-size-12">
                                                    {{ $format.dateAndHour(data.item.lastAvailabilityChangedAt, 'yyyy-MM-dd') }}
                                                </div>
                                            </div>
                                        </template>
                                    </DataTable>
                                </b-collapse>
                            </template>
                        </b-table-lite>
                    </div>
                </template>
                <!-- <ModalEdit /> -->
            </template>
        </Card>
    </div>
</template>

<script>
    import { ISSUE_STATUS } from '@/services/catalogService';
    import camelCase from 'camelcase';
    import DataTable from '@/components/common/DataTable.vue';
    import BadgeHidden from '@/components/common/BadgeHidden.vue';
    // import ModalEdit from './parts/ModalEdit.vue';

    export default {
        name: 'ImportModerationDetails',
        components: {
            DataTable,
            BadgeHidden,
            // ModalEdit,
        },
        provide() {
            return {
                parentComponent: this,
            };
        },
        data() {
            return {
                id: this.$route.params.id,
                requestIssue: this.$req('catalog/importIssues'),
                requestIssueDetails: this.$req('catalog/importIssuesDetails'),
                requestOffer: this.$req('offers/offer'),
                requestVariantWithProduct: this.$req('catalog/variantWithProduct'),
            };
        },
        computed: {
            parentRoute() {
                return this.$route.params.parentUrl ? this.$router.resolve(this.$route.params.parentUrl).resolved : this.$route.matched.slice(-2)[0];
            },
            issue() {
                return this.requestIssueDetails.getData();
            },
            conflictedProducts() {
                return this.issue?.details.conflictedProducts;
            },
            variants() {
                return this.conflictedProducts?.map(item => ({ ...item, _showDetails: true })) || [];
            },
            variantsFields() {
                return Object.keys(this.conflictedProducts?.[0] || {}).filter(item => item !== 'variants');
            },
            offersFields() {
                return [
                    { key: 'product', label: this.$t('aggregateOfferId') },
                    { key: 'ean', label: this.$t('ean') },
                    { key: 'name', label: this.$t('name') },
                    { key: 'info', label: this.$t('info') },
                    { key: 'code', label: this.$t('code') },
                    { key: 'feed', label: this.$t('feedShop') },
                    { key: 'type', label: this.$t('offerType') },
                    { key: 'price', label: this.$t('price') },
                    { key: 'hidden', label: this.$t('hidden') },
                    { key: 'active', label: this.$t('availability') },
                ];
            },
        },
        watch: {
            conflictedProducts() {
                const gtins = Array.from(new Set(this.conflictedProducts.map(prod => prod.variants.map(variant => variant.gtin)).flat(10)));
                Promise.all(gtins.map(this.fetchOffers));
            },
        },
        methods: {
            camelCase,
            fetchIssue() {
                return this.requestIssueDetails.get({ urlParams: { id: this.id } }).catch(() => {});
            },
            fetchOffers(ean) {
                this.requestOffer
                    .id(ean)
                    .get({ requestParams: { ean } })
                    .catch(() => {});
            },
            getOffersLoading(gtins) {
                return gtins.map(gtin => this.requestOffer.getLoading(gtin)).filter(loading => !!loading);
            },
            getOffersError(gtins) {
                return gtins.map(gtin => this.requestOffer.getError(gtin)).filter(error => !!error);
            },
            createItemUrl(item) {
                return {
                    name: 'OffersDetails',
                    params: { id: item.id, parentUrl: this.$route.fullPath },
                    query: {},
                };
            },
            confirmCreate() {
                this.$bvModal
                    .msgBoxConfirm(this.$t('confirmCreationAProductFromSuggestion'), {
                        title: this.$t('createProduct'),
                        okTitle: this.$t('create'),
                        cancelTitle: this.$t('cancel'),
                        centered: true,
                    })
                    .then(value => {
                        if (value) {
                            const p1 = this.requestVariantWithProduct.post({ requestParams: {} });
                            const p2 = this.requestIssue.id(`patch-${this.id}`).patch({ urlParams: { id: this.id }, requestParams: { status: ISSUE_STATUS.CLOSED } });
                            Promise.all([p1, p2])
                                .then(() => {
                                    this.$toastr.success(this.$t('dataSavedSuccessfully'));
                                    this.$router.push(this.parentRoute);
                                })
                                .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
                        }
                    });
            },
            confirmIgnore() {
                this.$bvModal
                    .msgBoxConfirm(this.$t('confirmIgnoringSuggestion'), {
                        title: this.$t('ignoreSuggestion'),
                        okTitle: this.$t('ignore'),
                        cancelTitle: this.$t('cancel'),
                        centered: true,
                    })
                    .then(value => {
                        if (value) {
                            this.requestIssue
                                .id(`patch-${this.id}`)
                                .patch({ urlParams: { id: this.id }, requestParams: { status: ISSUE_STATUS.WONT_DO } })
                                .then(data => {
                                    this.$toastr.success(this.$t('proposalHasBeenSuccessfullyIgnored'));
                                    this.$router.push(this.parentRoute);
                                })
                                .catch(error => this.$toastr.error(error.message, this.$t('errorOccurred')));
                        }
                    });
            },
            confirmEdit() {
                this.$root.$emit('bv::show::modal', 'modal-import-moderation-issue-edit');
            },
        },
        created() {
            this.fetchIssue();
        },
    };
</script>

<style lang="scss" scoped>
    .grid-template {
        display: grid;
        grid-template-columns: minmax(60px, max-content) auto;
        column-gap: 1rem;
        row-gap: 0.4rem;
        align-items: center;

        .label {
            font-weight: bold;
            margin-bottom: auto;
        }
    }
</style>
